import { useState } from 'react';
import { CATEGORIES, COLOUR_PALETTE, EMOJI_PICKER, type CardTemplate } from '@nudge/shared';
import { BottomSheet } from '../ui/BottomSheet';

// These match the DB column widths in the card_template table
const MAX_LABEL_LENGTH = 40;
const MAX_SUBTEXT_LENGTH = 80;

interface CardEditorProps {
  open: boolean;
  initial?: CardTemplate;
  onClose: () => void;
  onSubmit: (values: { icon: string; label: string; subtext: string; category: string; colour: string | null; is_shared: boolean }) => Promise<void>;
}

export function CardEditor({ open, initial, onClose, onSubmit }: CardEditorProps) {
  const [icon, setIcon] = useState(initial?.icon ?? '💚');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [subtext, setSubtext] = useState(initial?.subtext ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'connection');
  const [colour, setColour] = useState<string | null>(initial?.colour ?? null);
  const [isShared, setIsShared] = useState(initial?.is_shared ?? true);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try {
      await onSubmit({ icon, label: label.trim(), subtext: subtext.trim(), category, colour, is_shared: isShared });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose}>
      <form onSubmit={submit} className="max-h-[80vh] overflow-y-auto px-6 pb-8 pt-2">
        <h3 className="text-lg font-medium text-text-primary">{initial ? 'Edit card' : 'New card'}</h3>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-text-muted">Icon</label>
          <div className="mt-2 max-h-32 overflow-y-auto rounded-xl border border-border-subtle bg-card p-2">
            <div className="grid grid-cols-8 gap-1">
              {EMOJI_PICKER.map((e) => (
                <button type="button" key={e} onClick={() => setIcon(e)}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-xl ${icon === e ? 'bg-sage/20 ring-1 ring-sage' : ''}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-text-muted">Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={MAX_LABEL_LENGTH}
            placeholder="Need quiet"
            className="mt-1 w-full rounded-xl border border-border-subtle bg-card px-3 py-2 text-sm text-text-primary outline-none focus:border-sage" />
        </div>

        <div className="mt-3">
          <label className="text-xs uppercase tracking-wide text-text-muted">Subtext (optional)</label>
          <input value={subtext} onChange={(e) => setSubtext(e.target.value)} maxLength={MAX_SUBTEXT_LENGTH}
            placeholder="Headphones in"
            className="mt-1 w-full rounded-xl border border-border-subtle bg-card px-3 py-2 text-sm text-text-primary outline-none focus:border-sage" />
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-text-muted">Category</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.values(CATEGORIES).map((c) => (
              <button type="button" key={c.key} onClick={() => setCategory(c.key)}
                className={`rounded-full px-3 py-1.5 text-xs ${category === c.key ? 'bg-sage text-white' : 'border border-border-subtle bg-card text-text-muted'}`}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-text-muted">Accent colour</label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setColour(null)} aria-label="No colour"
              className={`h-8 w-8 rounded-full border ${colour === null ? 'border-sage ring-2 ring-sage' : 'border-border-subtle'}`}>
              <span className="block h-full w-full rounded-full bg-card" />
            </button>
            {COLOUR_PALETTE.map((c) => (
              <button type="button" key={c} onClick={() => setColour(c)} style={{ backgroundColor: c }} aria-label={c}
                className={`h-8 w-8 rounded-full ${colour === c ? 'ring-2 ring-white ring-offset-2 ring-offset-surface' : ''}`} />
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-border-subtle bg-card px-4 py-3">
          <div>
            <div className="text-sm text-text-primary">Share with circle</div>
            <div className="text-xs text-text-muted">Off = personal card, only you can use it</div>
          </div>
          <button type="button" role="switch" aria-checked={isShared} onClick={() => setIsShared((v) => !v)}
            className={`relative h-6 w-11 rounded-full ${isShared ? 'bg-sage' : 'bg-border-strong'}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${isShared ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <button type="submit" disabled={busy || !label.trim()}
          className="mt-6 w-full rounded-xl bg-sage py-3 text-base font-medium text-white disabled:opacity-50">
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create card'}
        </button>
        <button type="button" onClick={onClose} className="mt-2 w-full py-2 text-sm text-text-muted">Cancel</button>
      </form>
    </BottomSheet>
  );
}
